import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import {
  creatableLocationKindsOf,
  type ICreatableLocationKinds,
} from '~/client/src/shared/resources/domain/creatableLocationKinds'
import type ProjectResourcesStore from '~/client/src/shared/stores/domain/ProjectResources.store'

import { useLocationProjectId } from './useLocationResourceStore'

/**
 * The kinds a new location may have in a project, the active one unless given. A failed load is
 * retried once: nothing else reloads it until a location property or template changes. Call from
 * an observer.
 */
export function useCreatableLocationKinds(
  projectId?: string,
): ICreatableLocationKinds {
  const id = useLocationProjectId(projectId)
  const { locationResourcesStore: store } = React.useContext(
    MobXProviderContext,
  ) as {
    locationResourcesStore?: Pick<
      ProjectResourcesStore<unknown, unknown>,
      'propertiesStateFor' | 'refreshSchema'
    >
  }
  const propertiesState = id ? store.propertiesStateFor(id) : null
  const propertiesError = propertiesState?.propertiesError

  const retriedFor = React.useRef<string>()
  React.useEffect(() => {
    if (!id || !propertiesError || retriedFor.current === id) return
    retriedFor.current = id
    if (store.propertiesStateFor(id).propertiesError) {
      void store.refreshSchema(id)
    }
  }, [id, propertiesError, store])

  return React.useMemo(
    () => creatableLocationKindsOf(propertiesState),
    [propertiesState],
  )
}
